import { t } from "@lingui/macro";
import {
  BillingPlanIssueInvoicesOptionsDocument,
  type BillingPlanIssueInvoicesOptionsQuery,
  type BillingPlanIssueInvoicesOptionsQueryVariables,
  type ProjectBillingPlanSettingGenerateInvoiceItemsEnum,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit/Select";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import type { TToFieldStates } from "@src/utils/forms/ts-utils";
import { required } from "@src/utils/forms/validators";
import mapToOptions from "@src/utils/map-to-options";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";
import type { ChangeEvent } from "react";

type BillingEntity = NonNullable<
  NonNullable<
    NonNullable<BillingPlanIssueInvoicesOptionsQuery["project"]>["brand"]
  >["client"]["billingEntities"]
>[0];

type Contact = NonNullable<
  NonNullable<
    NonNullable<BillingPlanIssueInvoicesOptionsQuery["project"]>["brand"]
  >["contacts"]
>[0];

type ExchangeRate = NonNullable<
  NonNullable<
    NonNullable<BillingPlanIssueInvoicesOptionsQuery["project"]>["brand"]
  >["client"]["currencyExchangeRates"]
>[0];

type VatRate = NonNullable<BillingPlanIssueInvoicesOptionsQuery["vatRates"]>[0];

type Params = {
  generateInvoiceItems:
    | ProjectBillingPlanSettingGenerateInvoiceItemsEnum
    | undefined
    | null;
  vatRateId: string;
  dateOfSupplyFromIssueDate: string;
  isChecked: boolean;
  workspace_company_id: string;
  workspace_bank_account_id: string | undefined;
  // invoice_sequence_no: string;
  billing_entity_id: string;
  contact_id: string;
  order_id: string;
  text_on_invoice: string;
  note: string;
  internal_note: string;
  create_draft_invoice: boolean;
  create_summary_invoice_after_payment: boolean;
};
export class IssueInvoicesForm extends FormState<TToFieldStates<Params>> {
  popoverState = new DisclosureState();
  contacts: Contact[] = [];
  ordersOptions: IOption[] = [];
  appStore: AppStore;
  projectId: string;
  billingEntities: BillingEntity[] = [];
  clientExchangeRates: ExchangeRate[] = [];
  vatRates: VatRate[] = [];

  private requiredIfChecked = (
    $:
      | string
      | ProjectBillingPlanSettingGenerateInvoiceItemsEnum
      | number
      | null
      | undefined,
  ): string | null => {
    if (!this.$.isChecked.$) return null;
    return required($ === null ? undefined : $);
  };

  private billingEntityValidator = (
    billingEntityId: string | null | undefined,
  ): string | null => {
    if (!this.$.isChecked.$) return null;
    if (!billingEntityId) return null;

    const billingEntity = this.billingEntities.find(
      (entity) => entity.id === billingEntityId,
    );

    if (!billingEntity) return null;

    const billingEntityCurrency = billingEntity.defaultCurrency;
    const workspaceCurrency = this.appStore.workspaceStore.settings?.currency;

    if (!billingEntityCurrency || !workspaceCurrency) return null;

    // Check if currencies match
    if (billingEntityCurrency.id === workspaceCurrency.id) return null;

    // Currency mismatch detected - check for exchange rate
    const hasExchangeRate = this.clientExchangeRates.some(
      (rate) => rate.currency.id === billingEntityCurrency.id,
    );

    if (!hasExchangeRate) {
      return t`The conversion rate is not set for this client. Please complete it to continue.`;
    }

    return null;
  };

  private issueInvoicesOptionsFetcher = new FetchHelper<
    BillingPlanIssueInvoicesOptionsQuery,
    BillingPlanIssueInvoicesOptionsQueryVariables
  >(BillingPlanIssueInvoicesOptionsDocument);

  constructor(args: Params, projectId: string, appStore: AppStore) {
    super({
      isChecked: new FieldState(args.isChecked),
      generateInvoiceItems: new FieldState(args.generateInvoiceItems),
      vatRateId: new FieldState(args.vatRateId),
      dateOfSupplyFromIssueDate: new FieldState(args.dateOfSupplyFromIssueDate),
      workspace_company_id: new FieldState(args.workspace_company_id),
      workspace_bank_account_id: new FieldState(args.workspace_bank_account_id),
      // invoice_sequence_no: new FieldState(args.invoice_sequence_no),
      billing_entity_id: new FieldState(args.billing_entity_id),
      contact_id: new FieldState(args.contact_id),
      order_id: new FieldState(args.order_id),
      text_on_invoice: new FieldState(args.text_on_invoice),
      note: new FieldState(args.note),
      internal_note: new FieldState(args.internal_note),
      create_draft_invoice: new FieldState(args.create_draft_invoice),
      create_summary_invoice_after_payment: new FieldState(
        args.create_summary_invoice_after_payment,
      ),
    });
    makeObservable(this, {
      isDirty: computed,
      formHasError: computed,
      contacts: observable.ref,
      contactsOptions: computed,
      selectedContactEmail: computed,
      billingEntities: observable.ref,
      billingEntitiesOptions: computed,
      clientExchangeRates: observable.ref,
      hasCurrencyMismatch: computed,
      handleSwitchToggle: action.bound,
      handlePopoverOpen: action.bound,
      handlePopoverClose: action.bound,
      fetchIssueInvoicesOptions: action.bound,
      handleBillingEntityChange: action.bound,
    });
    this.appStore = appStore;
    this.projectId = projectId;
    this.$.generateInvoiceItems.validators(this.requiredIfChecked);
    this.$.vatRateId.validators(this.requiredIfChecked);
    this.$.dateOfSupplyFromIssueDate.validators(this.requiredIfChecked);
    this.$.workspace_company_id.validators(this.requiredIfChecked);
    this.$.workspace_bank_account_id.validators(this.requiredIfChecked);
    this.$.billing_entity_id.validators(
      this.requiredIfChecked,
      this.billingEntityValidator,
    );
    this.$.contact_id.validators(this.requiredIfChecked);
    this.fetchIssueInvoicesOptions(projectId);
  }

  get formHasError(): boolean {
    return this.$.isChecked.value && this.hasError;
  }

  get hasCurrencyMismatch(): boolean {
    if (!this.$.isChecked.value) return false;

    const selectedBillingEntity = this.billingEntities.find(
      (entity) => entity.id === this.$.billing_entity_id.value,
    );

    if (!selectedBillingEntity) return false;

    const billingEntityCurrency = selectedBillingEntity.defaultCurrency;
    const workspaceCurrency = this.appStore.workspaceStore.settings?.currency;

    if (!billingEntityCurrency || !workspaceCurrency) {
      return false;
    }

    return billingEntityCurrency.id !== workspaceCurrency.id;
  }

  get isDirty(): boolean {
    return (
      !!this.$.generateInvoiceItems.dirty ||
      !!this.$.isChecked.dirty ||
      !!this.$.workspace_company_id.dirty ||
      !!this.$.workspace_bank_account_id.dirty ||
      !!this.$.billing_entity_id.dirty ||
      !!this.$.contact_id.dirty ||
      !!this.$.order_id.dirty ||
      !!this.$.text_on_invoice.dirty ||
      !!this.$.note.dirty ||
      !!this.$.internal_note.dirty ||
      !!this.$.create_draft_invoice.dirty ||
      !!this.$.create_summary_invoice_after_payment.dirty
    );
  }

  handleSwitchToggle({ target }: ChangeEvent<HTMLInputElement>) {
    this.$.isChecked.onChange(target.checked);
    if (!target.checked) {
      this.$.generateInvoiceItems.reset(null);
      this.$.workspace_company_id.reset("");
      this.$.workspace_bank_account_id.reset("");
      this.$.dateOfSupplyFromIssueDate.reset("");
      this.$.billing_entity_id.reset("");
      this.$.contact_id.reset("");
      this.$.order_id.reset("");
      this.$.text_on_invoice.reset("");
      this.$.note.reset("");
      this.$.internal_note.reset("");
      this.$.create_draft_invoice.reset(false);
      this.$.create_summary_invoice_after_payment.reset(false);
      return;
    }
    this.handlePopoverOpen();
  }

  handlePopoverOpen() {
    this.popoverState.open();
  }

  handlePopoverClose() {
    this.popoverState.close();
  }

  get isLoadingOptions(): boolean {
    return this.issueInvoicesOptionsFetcher.isLoading.value;
  }

  handleBillingEntityChange(value: string | undefined) {
    this.$.billing_entity_id.onChange(value ?? "");

    const billingEntityAppliesVat =
      this.billingEntities.find(
        (entity) => entity.id === this.$.billing_entity_id.value,
      )?.applies_vat ?? false;

    const defaultVatRateId = this.vatRates.find((rate) => rate.default)?.id;
    const noVatRateId = this.vatRates.find((rate) => rate.amount === 0)?.id;

    this.$.vatRateId.onChange(
      (billingEntityAppliesVat ? defaultVatRateId : noVatRateId) ?? "",
    );

    if (!this.hasCurrencyMismatch) return;
    this.$.create_draft_invoice.onChange(true);
  }

  async fetchIssueInvoicesOptions(projectId: string) {
    const [data, error] = await this.issueInvoicesOptionsFetcher.fetch({
      projectId,
    });

    if (error) return;

    this.billingEntities = data.project?.brand?.client.billingEntities ?? [];
    this.clientExchangeRates =
      data.project?.brand?.client.currencyExchangeRates ?? [];
    this.contacts = data.project?.brand?.contacts ?? [];
    this.ordersOptions =
      data.project?.ordersSimpleMap.map((order) => ({
        label: order.purchase_order_no ?? "purchase-order",
        value: order.id,
      })) ?? [];
    this.vatRates = data.vatRates ?? [];
  }

  get billingEntitiesOptions(): IOption[] {
    return mapToOptions.billingEntities.toOptions(this.billingEntities);
  }

  get contactsOptions(): IOption[] {
    return mapToOptions.contacts(this.contacts);
  }

  get selectedBillingEntityEmail(): string | null {
    const entity = this.billingEntities.find(
      (entity) => entity.id === this.$.billing_entity_id.value,
    );
    return entity?.invoice_email ?? null;
  }

  get selectedContactEmail(): string | null {
    const contact = this.contacts.find(
      (contact) => contact.id === this.$.contact_id.value,
    );
    return contact?.email ?? null;
  }
}
